import React, { useState, useEffect } from 'react';
import {
  Card,
  Switch,
  Input,
  Button,
  message,
  Alert,
  Space,
  Descriptions,
  Tag,
  Spin,
  Modal,
  Table,
  Popconfirm,
  Select,
  Form
} from 'antd';
import {
  RobotOutlined,
  CheckCircleOutlined,
  ToolOutlined,
  ReloadOutlined,
  SaveOutlined,
  ExclamationCircleOutlined,
  UserAddOutlined,
  DeleteOutlined,
  TeamOutlined
} from '@ant-design/icons';
import API from '../services/api';
import dayjs from 'dayjs';

const { TextArea } = Input;

const AdminAIAssistantConfig = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState('enabled');
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  
  // 白名单相关状态
  const [whitelistUsers, setWhitelistUsers] = useState([]);
  const [whitelistLoading, setWhitelistLoading] = useState(false);
  const [addUserModalVisible, setAddUserModalVisible] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [addUserLoading, setAddUserLoading] = useState(false);
  
  const [form] = Form.useForm();

  // 获取AI助手配置
  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await API.get('/api/admin/ai-assistant/config');
      
      if (response.data.success) {
        const configData = response.data.data;
        setConfig(configData);
        setStatus(configData.status);
        setMaintenanceMessage(configData.maintenanceMessage || '');
        setWhitelistUsers(configData.whitelistUsers || []);
        setHasChanges(false);
      } else {
        message.error(response.data.message || '获取配置失败');
      }
    } catch (error) {
      console.error('获取配置错误:', error);
      message.error(error.response?.data?.message || '获取配置失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取所有用户列表（用于添加白名单）
  const fetchAllUsers = async () => {
    try {
      // 获取所有用户，使用较大的limit来获取全部
      const response = await API.get('/api/admin/users', {
        params: {
          page: 1,
          limit: 1000 // 获取足够多的用户
        }
      });
      
      if (response.data.users) {
        setAllUsers(response.data.users);
      } else if (response.data.success && response.data.data) {
        setAllUsers(response.data.data);
      }
    } catch (error) {
      console.error('获取用户列表错误:', error);
      message.error('获取用户列表失败');
    }
  };

  // 添加用户到白名单
  const addToWhitelist = async () => {
    if (!selectedUserId) {
      message.warning('请选择用户');
      return;
    }

    setAddUserLoading(true);
    try {
      const response = await API.post('/api/admin/ai-assistant/whitelist', {
        userId: selectedUserId
      });

      if (response.data.success) {
        message.success(response.data.message || '添加成功');
        setAddUserModalVisible(false);
        setSelectedUserId(null);
        form.resetFields();
        fetchConfig(); // 重新获取配置以更新白名单
      } else {
        message.error(response.data.message || '添加失败');
      }
    } catch (error) {
      console.error('添加白名单错误:', error);
      message.error(error.response?.data?.message || '添加失败');
    } finally {
      setAddUserLoading(false);
    }
  };

  // 从白名单移除用户
  const removeFromWhitelist = async (userId, username) => {
    setWhitelistLoading(true);
    try {
      const response = await API.delete(`/api/admin/ai-assistant/whitelist/${userId}`);

      if (response.data.success) {
        message.success(response.data.message || `用户 ${username} 已从白名单移除`);
        fetchConfig(); // 重新获取配置以更新白名单
      } else {
        message.error(response.data.message || '移除失败');
      }
    } catch (error) {
      console.error('移除白名单错误:', error);
      message.error(error.response?.data?.message || '移除失败');
    } finally {
      setWhitelistLoading(false);
    }
  };

  // 更新AI助手配置
  const updateConfig = async () => {
    if (!maintenanceMessage.trim() && status === 'maintenance') {
      message.warning('请输入维护提示信息');
      return;
    }

    setSaving(true);
    try {
      const response = await API.put('/api/admin/ai-assistant/config', {
        status,
        maintenanceMessage: maintenanceMessage.trim()
      });

      if (response.data.success) {
        message.success('配置已更新');
        setConfig(response.data.data);
        setHasChanges(false);
      } else {
        message.error(response.data.message || '更新配置失败');
      }
    } catch (error) {
      console.error('更新配置错误:', error);
      message.error(error.response?.data?.message || '更新配置失败');
    } finally {
      setSaving(false);
    }
  };

  // 快速切换状态
  const toggleStatus = () => {
    Modal.confirm({
      title: '确认切换状态',
      icon: <ExclamationCircleOutlined />,
      content: `确定要将AI助手切换为${status === 'enabled' ? '维护模式' : '正常模式'}吗？`,
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          const response = await API.post('/api/admin/ai-assistant/toggle');
          
          if (response.data.success) {
            message.success(response.data.message);
            setConfig(response.data.data);
            setStatus(response.data.data.status);
            setMaintenanceMessage(response.data.data.maintenanceMessage || '');
            setHasChanges(false);
          } else {
            message.error(response.data.message || '切换状态失败');
          }
        } catch (error) {
          console.error('切换状态错误:', error);
          message.error(error.response?.data?.message || '切换状态失败');
        }
      }
    });
  };

  // 监听表单变化
  useEffect(() => {
    if (config) {
      const statusChanged = status !== config.status;
      const messageChanged = maintenanceMessage !== (config.maintenanceMessage || '');
      setHasChanges(statusChanged || messageChanged);
    }
  }, [status, maintenanceMessage, config]);

  // 初始加载
  useEffect(() => {
    fetchConfig();
  }, []);

  if (loading && !config) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <Card
        title={
          <Space>
            <RobotOutlined style={{ fontSize: 20 }} />
            <span>AI 智能助手配置</span>
          </Space>
        }
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchConfig}
            loading={loading}
          >
            刷新
          </Button>
        }
      >
        {/* 当前状态概览 */}
        {config && (
          <Alert
            message="当前状态"
            description={
              <Descriptions column={2} size="small">
                <Descriptions.Item label="服务状态">
                  <Tag
                    icon={status === 'enabled' ? <CheckCircleOutlined /> : <ToolOutlined />}
                    color={status === 'enabled' ? 'success' : 'warning'}
                  >
                    {status === 'enabled' ? '正常运行' : '维护中'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="最后更新">
                  {config.updatedAt ? dayjs(config.updatedAt).format('YYYY-MM-DD HH:mm:ss') : '-'}
                </Descriptions.Item>
              </Descriptions>
            }
            type={status === 'enabled' ? 'success' : 'warning'}
            showIcon
            style={{ marginBottom: 24 }}
          />
        )}

        {/* 快速切换 */}
        <Card
          title="快速操作"
          size="small"
          style={{ marginBottom: 24 }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div>
              <Space>
                <span style={{ fontSize: 14, fontWeight: 500 }}>服务状态：</span>
                <Switch
                  checked={status === 'enabled'}
                  checkedChildren="正常"
                  unCheckedChildren="维护"
                  onChange={(checked) => setStatus(checked ? 'enabled' : 'maintenance')}
                />
                <Button
                  type="primary"
                  size="small"
                  onClick={toggleStatus}
                  style={{ marginLeft: 16 }}
                >
                  一键切换
                </Button>
              </Space>
              <p style={{ fontSize: 12, color: '#999', margin: '8px 0 0 0' }}>
                使用"一键切换"可以快速在正常模式和维护模式之间切换
              </p>
            </div>
          </Space>
        </Card>

        {/* 详细配置 */}
        <Card title="详细配置" size="small">
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            {/* 维护提示信息 */}
            <div>
              <div style={{ marginBottom: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 500 }}>维护提示信息</span>
                <span style={{ fontSize: 12, color: '#999', marginLeft: 8 }}>
                  (在维护模式下，用户将看到此信息)
                </span>
              </div>
              <TextArea
                rows={4}
                value={maintenanceMessage}
                onChange={(e) => setMaintenanceMessage(e.target.value)}
                placeholder="请输入维护提示信息，例如：AI助手正在维护升级中，预计30分钟后恢复。如有紧急问题，请提交工单或联系管理员。"
                maxLength={500}
                showCount
              />
            </div>

            {/* 操作按钮 */}
            <div style={{ display: 'flex', gap: 12 }}>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={updateConfig}
                loading={saving}
                disabled={!hasChanges}
              >
                保存配置
              </Button>
              <Button
                onClick={() => {
                  setStatus(config.status);
                  setMaintenanceMessage(config.maintenanceMessage || '');
                }}
                disabled={!hasChanges}
              >
                重置
              </Button>
            </div>

            {hasChanges && (
              <Alert
                message="有未保存的更改"
                description='您已修改配置但尚未保存，请点击"保存配置"按钮以应用更改。'
                type="warning"
                showIcon
                closable
              />
            )}
          </Space>
        </Card>
      </Card>

      {/* 白名单用户管理 */}
      <Card
        title={
          <Space>
            <TeamOutlined style={{ fontSize: 20 }} />
            <span>白名单用户管理</span>
            <Tag color="blue">{whitelistUsers.length} 人</Tag>
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            onClick={() => {
              setAddUserModalVisible(true);
              fetchAllUsers();
            }}
          >
            添加用户
          </Button>
        }
        style={{ marginTop: 24 }}
      >
        <Alert
          message="白名单用户在维护模式下仍可正常使用AI助手"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        <Table
          dataSource={whitelistUsers}
          loading={whitelistLoading}
          rowKey="_id"
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
            showTotal: (total) => `共 ${total} 个用户`
          }}
          columns={[
            {
              title: '用户名',
              dataIndex: 'username',
              key: 'username',
              width: 150
            },
            {
              title: '邮箱',
              dataIndex: 'email',
              key: 'email',
              ellipsis: true
            },
            {
              title: 'QQ',
              dataIndex: 'qq',
              key: 'qq',
              width: 120,
              render: (qq) => qq || '-'
            },
            {
              title: '角色',
              dataIndex: 'role',
              key: 'role',
              width: 100,
              render: (role) => {
                const roleMap = {
                  admin: { text: '管理员', color: 'red' },
                  user: { text: '用户', color: 'blue' },
                  'wlj-user': { text: 'WLJ用户', color: 'green' },
                  'pending-verification': { text: '待审核', color: 'orange' }
                };
                const roleInfo = roleMap[role] || { text: role, color: 'default' };
                return <Tag color={roleInfo.color}>{roleInfo.text}</Tag>;
              }
            },
            {
              title: '账户状态',
              dataIndex: 'status',
              key: 'status',
              width: 100,
              render: (status) => {
                if (!status) return '-';
                const statusMap = {
                  active: { text: '正常', color: 'success' },
                  suspended: { text: '停用', color: 'error' },
                  pending: { text: '待审核', color: 'warning' }
                };
                const statusInfo = statusMap[status] || { text: status, color: 'default' };
                return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
              }
            },
            {
              title: '操作',
              key: 'action',
              width: 100,
              fixed: 'right',
              render: (_, record) => (
                <Popconfirm
                  title="确认移除"
                  description={`确定要将 ${record.username} 从白名单移除吗？移除后该用户在维护模式下将无法访问AI助手。`}
                  onConfirm={() => removeFromWhitelist(record._id, record.username)}
                  okText="确定"
                  cancelText="取消"
                >
                  <Button
                    type="link"
                    danger
                    icon={<DeleteOutlined />}
                    size="small"
                  >
                    移除
                  </Button>
                </Popconfirm>
              )
            }
          ]}
          locale={{
            emptyText: '暂无白名单用户，点击右上角"添加用户"按钮添加'
          }}
        />
      </Card>

      {/* 添加用户到白名单的Modal */}
      <Modal
        title="添加用户到白名单"
        open={addUserModalVisible}
        onOk={addToWhitelist}
        onCancel={() => {
          setAddUserModalVisible(false);
          setSelectedUserId(null);
          form.resetFields();
        }}
        confirmLoading={addUserLoading}
        okText="添加"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="选择用户"
            name="userId"
            rules={[{ required: true, message: '请选择用户' }]}
          >
            <Select
              showSearch
              placeholder="搜索用户名、邮箱或QQ号"
              optionFilterProp="children"
              onChange={(value) => setSelectedUserId(value)}
              filterOption={(input, option) => {
                const searchText = input.toLowerCase();
                const label = (option?.label ?? '').toLowerCase();
                const username = (option?.username ?? '').toLowerCase();
                const email = (option?.email ?? '').toLowerCase();
                const qq = (option?.qq ?? '').toLowerCase();
                
                return label.includes(searchText) || 
                       username.includes(searchText) || 
                       email.includes(searchText) ||
                       qq.includes(searchText);
              }}
              options={allUsers
                .filter(user => !whitelistUsers.find(wu => wu._id === user._id))
                .map(user => {
                  const roleMap = {
                    admin: '管理员',
                    user: '用户',
                    'wlj-user': 'WLJ用户',
                    'pending-verification': '待审核'
                  };
                  const roleText = roleMap[user.role] || user.role;
                  const qqText = user.qq ? ` | QQ: ${user.qq}` : '';
                  
                  return {
                    label: `${user.username} (${user.email})${qqText} - ${roleText}`,
                    value: user._id,
                    username: user.username,
                    email: user.email,
                    qq: user.qq || ''
                  };
                })}
            />
          </Form.Item>
          
          {allUsers.length === 0 && (
            <Alert
              message="正在加载用户列表..."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          
          {allUsers.length > 0 && (
            <Alert
              message={`共 ${allUsers.length} 个用户，其中 ${whitelistUsers.length} 个已在白名单中`}
              type="info"
              showIcon
            />
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default AdminAIAssistantConfig;

